import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link
} from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import EventsList from './components/EventsList';
import EventDetail from './components/EventDetail';
import VideoGallery from './components/VideoGallery';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Signup from './components/Signup';
import CreateEvent from './components/CreateEvent';
import ForgotPassword from './components/ForgotPassword';
import AccountSettings from './components/AccountSettings';
import EventManagement from './components/EventManagement';
import UserManagement from './components/UserManagement';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import Footer from './components/Footer';
import './styles/index.css';

function NavBar() {
  const { t, i18n } = useTranslation();
  const { currentUser, isAdmin, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className="navbar-modern">
      <div className="navbar-container">
        <Link className="navbar-brand-modern" to="/" onClick={closeMobileMenu}>
          <span className="brand-text">
            <span className="brand-tag">Tag</span><span className="brand-po">po</span>
          </span>
        </Link>

        {/* Mobile menu toggle button */}
        <button
          className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        {/* Navigation links */}
        <div className={`navbar-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <Link className="nav-link-modern" to="/" onClick={closeMobileMenu}>{t('home')}</Link>
          <Link className="nav-link-modern" to="/events" onClick={closeMobileMenu}>{t('events')}</Link>
          <Link className="nav-link-modern" to="/videos" onClick={closeMobileMenu}>{t('videos')}</Link>
          {isAdmin && (
            <Link className="nav-link-modern nav-link-admin" to="/admin/users" onClick={closeMobileMenu}>{t('admin_users')}</Link>
          )}
        </div>

        {/* User actions */}
        <div className={`navbar-actions ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {currentUser ? (
            <>
              <Link to="/account" className="user-badge" onClick={closeMobileMenu}>
                {isAdmin ? '👑' : '👤'} {currentUser.email?.split('@')[0]}
              </Link>
              <button className="lang-btn" onClick={() => { handleLogout(); closeMobileMenu(); }}>{t('nav_logout')}</button>
            </>
          ) : (
            <>
              <Link className="lang-btn" to="/login" onClick={closeMobileMenu}>{t('nav_login')}</Link>
              <Link className="lang-btn" to="/signup" onClick={closeMobileMenu}>{t('nav_signup')}</Link>
            </>
          )}
          <div className="lang-toggle">
            <button className="lang-btn" onClick={() => changeLanguage('en')}>EN</button>
            <button className="lang-btn" onClick={() => changeLanguage('tl')}>TL</button>
          </div>
        </div>
      </div>
    </nav>
  );
}

function AppContent() {
  return (
    <div className="App">
      <NavBar />
      <main style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Routes>
          <Route path="/events" element={<EventsList />} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route path="/videos" element={<VideoGallery />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/create-event" element={<CreateEvent />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/account" element={<AccountSettings />} />
          <Route path="/admin/events/:id" element={<EventManagement />} />
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/" element={<LandingPage />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
        </Routes>
      </main>
      <Footer />
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
